<template>
  <div>
    <!-- 提交成功页面 -->
    <div v-if="submitted" class="sg-success">
      <div class="sg-success-icon">&#10003;</div>
      <div class="sg-success-title">提交成功</div>
      <div class="sg-success-desc">您的建议已提交，等待评审</div>
      <div class="sg-success-id">建议编号：{{ submittedId }}</div>
      <div class="sg-form" style="margin-top: 24px;">
        <button class="sg-btn sg-btn-primary" @click="resetForm">再提一条</button>
        <button class="sg-btn sg-btn-outline" style="margin-top: 12px;" @click="viewMySuggestions">查看我的建议</button>
      </div>
    </div>

    <!-- 表单页面 -->
    <template v-else>
      <div class="sg-header" style="position: relative;">
        <span
          class="sg-navbar-back"
          style="position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #fff;"
          @click="goBack"
        >&#8249;</span>
        <h1>合理化建议</h1>
        <p>提出你的改进建议</p>
      </div>

      <div class="sg-loading" v-if="loading">
        <div class="sg-spinner"></div>
        加载中...
      </div>

      <div class="sg-form" v-else>
        <div class="sg-form-group">
          <label class="sg-form-label">标题 <span class="required">*</span></label>
          <input
            class="sg-input"
            v-model="title"
            placeholder="请输入建议标题"
            maxlength="100"
          />
        </div>

        <div class="sg-form-group">
          <label class="sg-form-label">分类 <span class="required">*</span></label>
          <div class="sg-categories">
            <span
              v-for="cat in categories"
              :key="cat"
              class="sg-category-tag"
              :class="{ active: selectedCategory === cat }"
              @click="selectedCategory = cat"
            >{{ cat }}</span>
          </div>
        </div>

        <div class="sg-form-group">
          <label class="sg-form-label">详细描述 <span class="required">*</span></label>
          <textarea
            class="sg-textarea"
            v-model="description"
            placeholder="请详细描述您的建议内容..."
            maxlength="2000"
          ></textarea>
        </div>

        <div class="sg-form-group">
          <label class="sg-form-label">拍照上传</label>
          <div class="sg-upload-area" @click="triggerFileInput">
            <div class="sg-upload-icon">&#128247;</div>
            <div class="sg-upload-text">{{ uploading ? '上传中...' : '点击拍照或选择图片' }}</div>
          </div>
          <div class="sg-image-list" v-if="images.length">
            <div class="sg-image-item" v-for="(img, idx) in images" :key="idx">
              <img :src="img" alt="预览" />
              <span class="sg-image-remove" @click="removeImage(idx)">&times;</span>
            </div>
          </div>
        </div>

        <button
          class="sg-btn sg-btn-primary"
          :disabled="submitting || uploading"
          @click="handleSubmit"
        >{{ submitting ? '提交中...' : '提交建议' }}</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const categories = ref([]);
const userInfo = ref({});
const selectedCategory = ref('');
const title = ref('');
const description = ref('');
const scanLocation = ref('');
const images = ref([]);
const uploading = ref(false);
const submitting = ref(false);
const submitted = ref(false);
const submittedId = ref('');
const loading = ref(true);

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  scanLocation.value = params.get('location') || '';
  try {
    const data = await frappe.xcall('suggestion_system.api.collection.get_scan_page_data');
    categories.value = data.categories || [];
    userInfo.value = data;
  } catch (e) {
    frappe.show_alert({ message: '加载数据失败，请刷新重试', indicator: 'red' });
  } finally {
    loading.value = false;
  }
});

function triggerFileInput() {
  if (uploading.value) return;
  // 每次点击动态创建 input，避免浏览器缓存和 Vue ref 绑定时机问题
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.capture = 'environment';
  // 不设置 multiple，capture + multiple 在部分移动端浏览器不兼容
  input.style.display = 'none';
  document.body.appendChild(input);

  input.addEventListener('change', async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      document.body.removeChild(input);
      return;
    }
    uploading.value = true;
    try {
      const results = await Promise.allSettled(files.map(uploadFile));
      results.forEach((r) => {
        if (r.status === 'fulfilled') images.value.push(r.value);
      });
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) {
        frappe.show_alert({ message: failed + ' 张图片上传失败', indicator: 'orange' });
      }
    } finally {
      uploading.value = false;
      // 清理 DOM
      if (input.parentNode) document.body.removeChild(input);
    }
  }, { once: true });

  // 处理用户取消选择的情况
  input.addEventListener('cancel', () => {
    setTimeout(() => {
      if (input.parentNode) document.body.removeChild(input);
    }, 300);
  }, { once: true });

  input.click();
}

function removeImage(idx) {
  images.value.splice(idx, 1);
}

async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_private', '0');
  const response = await fetch('/api/method/frappe.handler.upload_file', {
    method: 'POST',
    body: formData,
    headers: { 'X-Frappe-CSRF-Token': frappe.csrf_token },
  });
  const data = await response.json();
  if (data && data.message && data.message.file_url) {
    return data.message.file_url;
  }
  throw new Error('上传失败');
}

async function handleSubmit() {
  if (!title.value.trim()) {
    frappe.show_alert({ message: '请输入建议标题', indicator: 'orange' });
    return;
  }
  if (!selectedCategory.value) {
    frappe.show_alert({ message: '请选择分类', indicator: 'orange' });
    return;
  }
  if (!description.value.trim()) {
    frappe.show_alert({ message: '请输入详细描述', indicator: 'orange' });
    return;
  }
  submitting.value = true;
  try {
    const result = await frappe.xcall('suggestion_system.api.collection.submit_suggestion', {
      title: title.value.trim(),
      category: selectedCategory.value,
      description: description.value.trim(),
      scan_location: scanLocation.value,
      image_urls: images.value.join(','),
    });
    submitted.value = true;
    submittedId.value = (result && (result.suggestion_id || result.name)) || '';
  } catch (e) {
    // frappe 已处理错误提示
  } finally {
    submitting.value = false;
  }
}

function resetForm() {
  title.value = '';
  description.value = '';
  selectedCategory.value = '';
  images.value = [];
  submitted.value = false;
  submittedId.value = '';
}

function viewMySuggestions() {
  window.location.href = '/suggestion-portal';
}

function goBack() {
  window.history.back();
}
</script>
